import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import axios from 'axios';
import { GlassButton } from '../ui';
import { HOST_STYLED } from './PropertiesPanel';
import type { ImportedSection } from '../../services/api';
import type { ComponentCategory } from './types';

/*
 * "Sections on this page": the left-rail list of every AIME section inserted on the page shown in
 * the imported-site editor (imported-editor-followups Phase 3). It is the only way to reach a section
 * that renders nothing on the page (an anchor that no longer exists, a block emptied by an old
 * editor) and it also lists blog_index. Every action reuses an existing editor handler/endpoint.
 *
 * Inline styles + CSS variables only (like PageListPanel); never hardcode colours.
 */

// ---------------------------------------------------------------------------------------------
// Pure helpers, shared with ImportedSiteEditor and unit-tested in __tests__/ImportedSectionList.
// ---------------------------------------------------------------------------------------------

/** Mirrors the server's `_HOST_TEXT_BODY_MAX` (site_builder_routes.py): the RAW body limit, which the
 *  server checks BEFORE sanitising, i.e. on the already-escaped `<p>…</p>` string. */
export const HOST_TEXT_BODY_MAX = 50000;

/** Python's str.strip() whitespace set, NOT JS trim()'s: Python strips \x1c-\x1f and U+0085 but not
 *  U+FEFF; JS trim() is the reverse. The server's `_host_text_is_blank` is the single definition, so
 *  blankness is decided with Python's set here (the BLANK_TABLE parity test covers both code points). */
const PY_WS = '\\t\\n\\v\\f\\r \\x1c-\\x1f\\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const PY_STRIP_RE = new RegExp('^[' + PY_WS + ']+|[' + PY_WS + ']+$', 'g');
const pyStrip = (s: string): string => s.replace(PY_STRIP_RE, '');

/** Visible text of an HTML fragment, mirroring BeautifulSoup(html, "html.parser").get_text():
 *  entities decoded, comments ignored, script/style/template content ignored. DOMParser documents
 *  are inert (no script execution, no resource loads). */
const htmlText = (html: string): string => {
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, style, template').forEach(n => n.remove());
    return doc.documentElement ? doc.documentElement.textContent || '' : '';
  }
  return html.replace(/<[^>]*>/g, '');
};

/**
 * True when a content_host_text block has nothing visible. MUST mirror the server's single
 * definition, `_host_text_is_blank` (site_builder_routes.py): heading not a non-blank string AND the
 * body has no non-whitespace text once tags are stripped. A non-object `controls`, or a non-string
 * heading/body, is BLANK (never stringified into "visible" text). The parity test runs the server's
 * own BLANK_TABLE (tests/test_imported_host_text_placeholder.py) against this function.
 */
export const isHostTextBlank = (controls: unknown): boolean => {
  if (!controls || typeof controls !== 'object' || Array.isArray(controls)) return true;
  const c = controls as Record<string, unknown>;
  if (typeof c.heading === 'string' && pyStrip(c.heading)) return false;
  const body = c.body;
  if (typeof body !== 'string' || !pyStrip(body)) return true;
  return !pyStrip(htmlText(body));
};

/** Does a stored section's `page` render on the current page? Mirrors `_patch_matches_page`: the
 *  page with slashes trimmed equals the current page, or is empty (legacy rows) while the current
 *  page is the entrypoint. `currentPageRel` is the editor's resolved page, never the raw "" URL. */
export const sectionMatchesPage = (page: string | undefined | null, currentPageRel: string, entrypoint: string): boolean => {
  const p = (page || '').replace(/^\/+|\/+$/g, '');
  if (!p) return currentPageRel === entrypoint;
  return p === currentPageRel;
};

/** Sections only interact (serve sorts by `order`, then each lands at its OWN anchor) when they share
 *  the same (anchor_selector, position). All append_body rows share the body; all page_content rows
 *  on a generated page form one group. */
export const sectionGroupKey = (s: Pick<ImportedSection, 'position' | 'anchor_selector'>): string =>
  `${s.position}|${s.position === 'before' || s.position === 'after' ? (s.anchor_selector || '') : ''}`;

export const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;')
   .replace(/</g, '&lt;')
   .replace(/>/g, '&gt;')
   .replace(/"/g, '&quot;')
   .replace(/'/g, '&#39;');

/** Plain text typed into the placeholder -> Host-Styled Text body. A blank line (Enter twice) starts a
 *  new paragraph; a single line break becomes <br>. Everything is escaped; the server's allowlist
 *  sanitiser stays authoritative. Whitespace-only text -> "". */
export const hostTextToBody = (text: string): string =>
  text.replace(/\r\n?/g, '\n')
    .split(/\n[^\S\n]*\n/)
    .map(t => t.trim())
    .filter(Boolean)
    .map(t => '<p>' + escapeHtml(t).replace(/[^\S\n]*\n[^\S\n]*/g, '<br>') + '</p>')
    .join('');

/** Length as the server counts it (Python len = code points, not UTF-16 units). */
export const pyLen = (s: string): number => Array.from(s).length;

/** Page sections in render order: the page filter above, stable sort by `order` (serve does the same). */
export const pageSectionsInOrder = (sections: ImportedSection[], currentPageRel: string, entrypoint: string): ImportedSection[] =>
  sections
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => sectionMatchesPage(s.page, currentPageRel, entrypoint))
    .sort((a, b) => ((a.s.order ?? 0) - (b.s.order ?? 0)) || (a.i - b.i))
    .map(({ s }) => s);

/** Pseudo component id of a copied host block (pinned to the backend's CLONE_COMPONENT_ID by a test). */
export const HOST_CLONE_ID = 'host_clone';
export const isCloneSection = (s: Pick<ImportedSection, 'component_id'>): boolean => s.component_id === HOST_CLONE_ID;

/** Tag of the first element of a copy's stored html ('' when there is none). */
export const cloneTagOf = (html: unknown): string => {
  if (typeof html !== 'string' || !html || typeof DOMParser === 'undefined') return '';
  const el = new DOMParser().parseFromString(html, 'text/html').body.firstElementChild;
  return el ? el.tagName.toLowerCase() : '';
};

export const cloneTagLabel = (tag: string): string => {
  if (tag === 'p') return 'Paragraph';
  if (/^h[1-6]$/.test(tag)) return 'Heading';
  if (tag === 'li') return 'List item';
  if (tag === 'ul' || tag === 'ol') return 'List';
  if (tag === 'blockquote') return 'Quote';
  return 'Block';
};

/** First `n` characters of a copy's visible text, whitespace collapsed ('' when empty). */
export const cloneTextPreview = (html: unknown, n: number): string =>
  typeof html === 'string' ? htmlText(html).replace(/\s+/g, ' ').trim().slice(0, n) : '';

export const REORDER_DISABLED_TITLE = 'Only sections added at the same spot can be reordered';
export const NOT_VISIBLE_TITLE = "This section isn't showing on the page — the spot it was added at isn't in this page's HTML. Delete it, or keep it if you expect the spot to come back.";

export const deleteConfirmText = (name: string, componentId: string): string =>
  `Delete “${name}” from this page? This can't be undone.`
  + (componentId === 'blog_index'
    ? ' Your blog page will show no post list until you add a Blog Index section again.'
    : '');

// ---------------------------------------------------------------------------------------------

interface Props {
  sections: ImportedSection[];          // ALL sections from GET /sections; filtered here
  currentPageRel: string;               // ImportedSiteEditor's currentPageRel, never the raw currentPage
  entrypoint: string;
  selectedId: string | null;
  visibleIds: Set<string> | null;       // null = unknown (stale iframe / not ready) -> no "Not visible" badge
  onSelect: (sectionId: string) => void;
  onMove: (sectionId: string, delta: -1 | 1) => void;
  onDelete: (sectionId: string) => void;
}

const getApiBase = () =>
  process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8001`;

const badge = (bg: string): React.CSSProperties => ({
  fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 'var(--radius-sm)',
  background: bg, color: 'var(--text)', border: '1px solid var(--stroke)', flexShrink: 0,
});

const iconBtn: React.CSSProperties = { height: 24, width: 24, padding: 4 };

const ImportedSectionList: React.FC<Props> = ({
  sections, currentPageRel, entrypoint, selectedId, visibleIds, onSelect, onMove, onDelete,
}) => {
  const [names, setNames] = useState<Record<string, string>>({});

  // Component display names: one fetch, same catalogue the properties panel uses.
  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('access_token');
    axios.get(`${getApiBase()}/site-builder/components`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { for: 'imported_all' },
    }).then(res => {
      if (!alive) return;
      const map: Record<string, string> = {};
      ((res && res.data && res.data.categories) || []).forEach((cat: ComponentCategory) => {
        (cat.components || []).forEach(c => { map[c.id] = c.name; });
      });
      setNames(map);
    }).catch(() => { /* fall back to ids */ });
    return () => { alive = false; };
  }, []);

  const rows = useMemo(
    () => pageSectionsInOrder(sections, currentPageRel, entrypoint),
    [sections, currentPageRel, entrypoint],
  );

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: 'var(--text-secondary)', padding: '10px 12px 4px' }}>
        Sections on this page
      </div>
      {rows.length === 0 && (
        <div style={{ padding: '4px 12px 10px', fontSize: 11, color: 'var(--text-secondary)' }}>
          No sections added to this page yet.
        </div>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Sections on this page">
        {rows.map((s, i) => {
          const isClone = isCloneSection(s);
          const cloneHtml = isClone ? s.controls?.html : undefined;
          const name = isClone ? 'Copied block' : (names[s.component_id] || s.component_id);
          const hostStyled = HOST_STYLED.has(s.component_id);
          const empty = hostStyled && isHostTextBlank(s.controls);
          const notVisible = !empty && visibleIds !== null && !visibleIds.has(s.section_id);
          const key = sectionGroupKey(s);
          const canUp = rows.slice(0, i).some(r => sectionGroupKey(r) === key);
          const canDown = rows.slice(i + 1).some(r => sectionGroupKey(r) === key);
          // Host-styled: the heading, else the start of the body text (a block typed into the
          // placeholder has no heading), else "(empty)". Anything else: its #anchor_id.
          const heading = hostStyled && typeof s.controls?.heading === 'string' ? s.controls.heading.trim() : '';
          const bodyText = hostStyled && !heading && !empty && typeof s.controls?.body === 'string'
            ? htmlText(s.controls.body.replace(/<br\s*\/?>|<\/(p|li|h[1-6]|blockquote)>/gi, ' $&')).replace(/\s+/g, ' ').trim().slice(0, 80) : '';
          const subtitle = isClone ? (cloneTextPreview(cloneHtml, 80) || '(empty)')
            : hostStyled ? (heading || bodyText || '(empty)') : `#${s.anchor_id || ''}`;
          const selected = s.section_id === selectedId;
          return (
            <li key={s.section_id} data-testid={`section-row-${s.section_id}`}
                style={{ background: selected ? 'var(--panel-active)' : 'transparent',
                         borderTop: '1px solid var(--stroke)' }}>
              <button onClick={() => onSelect(s.section_id)} aria-current={selected ? 'true' : undefined}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px 2px',
                               border: 'none', background: 'transparent', cursor: 'pointer', font: 'inherit' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                 fontSize: 12, fontWeight: selected ? 600 : 500, color: 'var(--text)' }}>
                    {name}
                  </span>
                  {isClone && (
                    <span style={badge('var(--panel-strong)')}>{cloneTagLabel(cloneTagOf(cloneHtml))}</span>
                  )}
                  {empty && (
                    <span style={badge('var(--color-warning-soft)')}
                          title="Nothing to show yet — click it on the page (or select it here) to add text.">
                      Empty
                    </span>
                  )}
                  {notVisible && (
                    <span style={badge('var(--color-warning-soft)')} title={NOT_VISIBLE_TITLE}>Not visible</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {subtitle}
                </div>
              </button>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2, padding: '0 8px 4px' }}>
                <span title={canUp ? 'Move up' : REORDER_DISABLED_TITLE}>
                  <GlassButton variant="ghost" size="sm" style={iconBtn} disabled={!canUp}
                    icon={<ArrowUp size={14} />}
                    title={canUp ? 'Move up' : REORDER_DISABLED_TITLE}
                    aria-label={canUp ? 'Move up' : REORDER_DISABLED_TITLE}
                    onClick={() => onMove(s.section_id, -1)} />
                </span>
                <span title={canDown ? 'Move down' : REORDER_DISABLED_TITLE}>
                  <GlassButton variant="ghost" size="sm" style={iconBtn} disabled={!canDown}
                    icon={<ArrowDown size={14} />}
                    title={canDown ? 'Move down' : REORDER_DISABLED_TITLE}
                    aria-label={canDown ? 'Move down' : REORDER_DISABLED_TITLE}
                    onClick={() => onMove(s.section_id, 1)} />
                </span>
                <GlassButton variant="ghost" size="sm" style={iconBtn}
                  icon={<Trash2 size={14} />} title="Delete section" aria-label="Delete section"
                  onClick={() => { if (window.confirm(deleteConfirmText(name, s.component_id))) onDelete(s.section_id); }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ImportedSectionList;
